import { createTextResolver, mountView, starterTheme } from "@e308/ux";
import { BrowserAppSource } from "./app-source.js";
import { projectApp } from "./app-view.js";
import { mountOnlineRoom } from "./online-app.js";
import "./styles.css";

const root = document.querySelector<HTMLElement>("#app");
if (!root) throw new TypeError("Kilnfront app root is missing");

const theme = document.createElement("style");
theme.textContent = starterTheme;
document.head.append(theme);

const parameters = new URLSearchParams(location.search);
let dispose: () => void;
if (parameters.has("room")) dispose = mountOnlineRoom(root, parameters);
else {
  const worker = new Worker(new URL("./host-worker.ts", import.meta.url), { type: "module" });
  const source = new BrowserAppSource(worker);
  const mount = mountView(root, {
    source,
    project: projectApp,
    resolver: createTextResolver({
      quantities: { format: (value: string) => value },
    }),
  });
  let previousPhase = source.getSnapshot().phase;
  let navigationSequence = 0;
  const unsubscribeFocus = source.subscribe(() => {
    const navigation = source.getSnapshot().navigation;
    if (navigation && navigation.sequence !== navigationSequence) {
      navigationSequence = navigation.sequence;
      requestAnimationFrame(() => {
        const tab = Array.from(root.querySelectorAll<HTMLButtonElement>('[role="tab"]')).find(
          (entry) => entry.textContent === navigation.guidance.tab,
        );
        tab?.click();
        requestAnimationFrame(() => {
          const nodes = Array.from(root.querySelectorAll<HTMLElement>("[data-e308-key]"));
          const target = nodes.find((entry) => entry.dataset.e308Key === navigation.guidance.focus);
          if (target) {
            target.tabIndex = target.tabIndex < 0 ? -1 : target.tabIndex;
            target.focus();
            target.scrollIntoView({ block: "end" });
          }
        });
      });
    }
    const phase = source.getSnapshot().phase;
    if (
      phase !== previousPhase &&
      (phase === "playing" || (phase === "lobby" && previousPhase === "playing"))
    ) {
      requestAnimationFrame(() => {
        const title = root.querySelector("h1");
        if (title) {
          title.tabIndex = -1;
          title.focus({ preventScroll: true });
          window.scrollTo(0, 0);
        }
      });
    }
    previousPhase = phase;
  });
  dispose = () => {
    unsubscribeFocus();
    mount.dispose();
    source.dispose();
  };
}

window.addEventListener(
  "pagehide",
  () => {
    dispose();
  },
  { once: true },
);
